import { Link } from "react-router-dom";
import {
  Truck,
  ShieldCheck,
  Package,
  CircleCheck,
  Sparkles,
  Calendar,
  Layers,
  Tag,
  Boxes,
  Recycle,
  Lock,
  FolderArchive,
  GlassWater,
  ArrowRight,
} from "lucide-react";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";
import { getYvelinesCityPageBySlug } from "@/data/yvelinesCitiesContent";

const cityNameToSlug = (name) =>
  name
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/'/g, "")
    .replace(/\s+/g, "-");

const Sections = ({ city }) => (
  <>
    {/* ======= SECTION 2 — Service de livraison à domicile à Trappes ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Livraison à domicile
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Notre service de livraison de cartons à domicile {city.aCity}
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Parce que votre temps est compté et que charger de grands
            paquets de cartons à plat dans votre voiture au milieu des axes
            urbains est une contrainte fatigante, notre service de
            livraison s&apos;occupe de tout.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {[
            {
              title: "Proximité immédiate",
              body: "Nous desservons quotidiennement l'intégralité des quartiers de Trappes (Les Merisiers, Élancourt-Trappes, La Plaine de Neauphle…) ainsi que les villes voisines comme Élancourt, Guyancourt, Montigny-le-Bretonneux, Plaisir et Bois-d'Arcy.",
              Icon: Truck,
            },
            {
              title: "Ponctualité garantie",
              body: "Pas de créneaux flous qui bloquent votre journée de travail ou vos préparatifs. Nous fixons ensemble un rendez-vous précis et rigoureusement respecté pour vous permettre de vous organiser à la minute près.",
              Icon: Calendar,
            },
            {
              title: "Hygiène & qualité",
              body: "Toutes vos fournitures vous sont remises à plat sous film de protection étanche, parfaitement propres et sèches, prêtes pour un pliage à haute résistance.",
              Icon: ShieldCheck,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="bg-white rounded-2xl border border-[#cfdbe6] p-5 md:p-6"
            >
              <div className="flex items-center gap-2.5">
                <span className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h3 className="font-display font-extrabold text-[15px] text-[#0f1d2b] leading-tight">
                  {title}
                </h3>
              </div>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ======= SECTION 3 — Cartons reconditionnés à -40% ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-7">
            <div className="overline" style={{ color: "#237cb5" }}>
              Éco-budget
            </div>
            <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Cartons reconditionnés à&nbsp;-40% : utilisés une seule fois
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Pour concilier transition écologique et gestion optimale de
              votre budget {city.aCity}, nous avons mis en place un
              circuit alternatif particulièrement avantageux.
            </p>

            <div className="mt-6 space-y-4">
              {[
                {
                  title: "Un état impeccable pour un second usage",
                  body: "Ces contenants proviennent exclusivement de lots industriels ou de transferts de bureaux n'ayant subi qu'un seul voyage. Ils ne sortent pas de circuits de stockage poussiéreux et conservent toute l'intégrité de leur fabrication d'origine.",
                  Icon: Recycle,
                },
                {
                  title: "Une charte de sélection drastique",
                  body: "Pas de mauvaise surprise : chaque boîte fait l'objet d'un examen minutieux à l'unité. Nos préparateurs vérifient la fermeté des parois et écartent le matériel marqué ou ramolli. Seuls les exemplaires offrant une géométrie parfaite intègrent nos stocks.",
                  Icon: ShieldCheck,
                },
                {
                  title: "Une tarification préférentielle à -40%",
                  body: "Vous profitez d'une remise substantielle sur ces contenants d'une robustesse professionnelle absolue. C'est l'alternative idéale pour emballer lourd tout en allégeant la facture finale de vos préparatifs.",
                  Icon: Tag,
                },
              ].map(({ title, body, Icon }) => (
                <div
                  key={title}
                  className="flex items-start gap-4 bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
                >
                  <span className="flex items-center justify-center shrink-0 h-11 w-11 rounded-full bg-[#237cb5] text-white">
                    <Icon className="h-5 w-5" strokeWidth={2.4} />
                  </span>
                  <div>
                    <h3 className="font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                      {title}
                    </h3>
                    <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                      {body}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <Link
              to="/cartons-reconditionnes"
              className="mt-7 inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
              data-testid="city-trappes-cta-reconditionnes"
            >
              <Recycle className="h-4 w-4" strokeWidth={2.6} />
              Voir les cartons reconditionnés
              <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
            </Link>
          </div>

          <div className="lg:col-span-5 bg-[#EEF3F7] border border-[#cfdbe6] rounded-3xl p-6">
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center h-12 w-12 rounded-full bg-[#237cb5] text-white">
                <Recycle className="h-6 w-6" strokeWidth={2.4} />
              </span>
              <div className="font-display font-extrabold text-[20px] text-[#0f1d2b] leading-tight">
                Économisez jusqu&apos;à&nbsp;40%
              </div>
            </div>
            <p className="mt-4 text-[14px] text-[#3a4856] leading-relaxed">
              Une seconde vie pour des cartons triés à la main, qui n&apos;ont
              voyagé qu&apos;une fois. Robustes, propres, prêts à
              l&apos;emploi.
            </p>
            <div className="mt-5 space-y-2.5 text-[13px] text-[#3a4856]">
              <div className="flex items-center gap-2">
                <CircleCheck className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Issus de lots industriels &amp; bureaux
              </div>
              <div className="flex items-center gap-2">
                <CircleCheck className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Contrôle qualité à l&apos;unité
              </div>
              <div className="flex items-center gap-2">
                <CircleCheck className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Double cannelure conservée
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Geste éco-responsable
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* ======= CTA Big Déménagement (au milieu) ======= */}
    <section className="py-12 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <BigDemenageurCTA testId={`city-${city.slug}-bigdem-cta-middle`} />
      </div>
    </section>

    {/* ======= SECTION 4 — Objets fragiles ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Objets fragiles
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Le bouclier antichoc pour votre vaisselle et vos écrans
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            La vaisselle, la verrerie fine, les miroirs et les
            équipements high-tech sont les premières victimes des
            déménagements mal préparés. Pour éviter les mauvaises
            surprises au déballage, notre gamme intègre des solutions
            techniques de pointe.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
          {[
            {
              title: "Cartons à croisillons spécifiques",
              body: "Indispensables pour la cuisine. Ils intègrent des alvéoles rigides qui isolent chaque verre et chaque assiette individuellement. Vos pièces ne s'entrechoquent plus pendant les virages ou les freinages du camion.",
              Icon: GlassWater,
            },
            {
              title: "Film à bulles haute densité & adhésifs",
              body: "Enveloppez vos cadres, bibelots et écrans d'ordinateur ou de télévision dans plusieurs couches de notre film à bulles d'air. Scellez le tout avec notre ruban adhésif technique à forte adhérence pour une immobilisation totale et une absorption parfaite des ondes de choc.",
              Icon: ShieldCheck,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="flex items-start gap-4 bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
            >
              <span className="flex items-center justify-center shrink-0 h-11 w-11 rounded-full bg-[#237cb5] text-white">
                <Icon className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <div>
                <h3 className="font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                  {title}
                </h3>
                <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                  {body}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8">
          <Link
            to="/emballage-protection"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-trappes-cta-fragile"
          >
            <Boxes className="h-4 w-4" strokeWidth={2.6} />
            Voir tous les emballages de protection
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
        </div>
      </div>
    </section>

    {/* ======= SECTION 5 — Cartons pour Archives ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Documents &amp; archives
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Structurez et sécurisez vos documents importants
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Que vous soyez un particulier protégeant ses documents
            administratifs (papiers d&apos;identité, actes de vente,
            impôts) ou une entreprise trappiste déplaçant sa comptabilité,
            le carton classique n&apos;est pas adapté au papier, qui est
            une matière extrêmement lourde et dense.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
          {[
            {
              title: "Carton Archives à poignées renforcées",
              body: "Conçu avec un format ergonomique spécifique, il évite l'accumulation d'un poids excessif difficile à soulever. Il possède des poignées latérales doublées pour assurer une prise ferme sans risque de déchirure lors du portage.",
              Icon: FolderArchive,
            },
            {
              title: "Résistance supérieure à l'écrasement vertical",
              body: "Sa structure en carton lourd double cannelure permet d'empiler plusieurs caisses d'archives les unes sur les autres au fond du camion ou dans votre futur local de stockage, sans que les boîtes du dessous ne s'enfoncent ou ne se déforment avec le temps.",
              Icon: Layers,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="flex items-start gap-4 bg-white border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
            >
              <span className="flex items-center justify-center shrink-0 h-11 w-11 rounded-full bg-[#237cb5] text-white">
                <Icon className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <div>
                <h3 className="font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                  {title}
                </h3>
                <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                  {body}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8">
          <Link
            to="/cartons-demenagement?categorie=archives"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-trappes-cta-archives"
          >
            <Lock className="h-4 w-4" strokeWidth={2.6} />
            Découvrir les cartons archives
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
        </div>
      </div>
    </section>

    {/* ======= SECTION 6 — Couverture département / Satellites ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Couverture {city.deptCode}
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Livraison de cartons de déménagement dans tout le 78
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Nous assurons la livraison de cartons de déménagement et de
            fournitures d&apos;emballage dans l&apos;ensemble des
            Yvelines. Que vous soyez un particulier ou un professionnel,
            vous pouvez recevoir rapidement le matériel nécessaire à la
            préparation de votre déménagement.
          </p>
          <p className="mt-4 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Découvrez ci-dessous les principales communes du département
            où nos services sont régulièrement disponibles à proximité de
            {" "}{city.name}&nbsp;:
          </p>
        </div>

        <ul className="mt-6 flex flex-wrap gap-x-5 gap-y-2.5 text-[15px] md:text-base">
          {city.nearbyCities.map((name) => {
            const idSlug = cityNameToSlug(name);
            const target = getYvelinesCityPageBySlug(idSlug);
            return (
              <li key={name}>
                {target ? (
                  <Link
                    to={`/carton-demenagement-${target.slug}`}
                    className="inline-flex items-center font-semibold text-[#237cb5] underline underline-offset-4 decoration-[#237cb5]/40 hover:decoration-[#237cb5] hover:text-[#0f1d2b] transition-colors"
                    data-testid={`city-trappes-nearby-${idSlug}`}
                  >
                    Carton déménagement {name}
                  </Link>
                ) : (
                  <span
                    className="inline-flex items-center font-semibold text-[#5b6b7a]"
                    data-testid={`city-trappes-nearby-${idSlug}`}
                  >
                    Carton déménagement {name}
                  </span>
                )}
              </li>
            );
          })}
        </ul>

        <div className="mt-8">
          <Link
            to={`/cartons-demenagement/${city.deptSlug}`}
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#EEF3F7] border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="city-trappes-cta-back-dept"
          >
            <Package className="h-4 w-4" strokeWidth={2.6} />
            Voir la page {city.deptName} ({city.deptCode})
          </Link>
        </div>
      </div>
    </section>
  </>
);

export default { Sections };
